import React, { useEffect, useState } from "react";
import { api } from "../../lib/nurseryApi";
import { CheckCircle, XCircle, Phone, EnvelopeSimple, MapPin, Globe, Copy, Trash, X, Clock, Star } from "@phosphor-icons/react";
import BackToOverview from "../../components/BackToOverview";

const STATUS_BADGE = {
  pending: { cls: "badge-warn", label: "pending" },
  approved: { cls: "badge-success", label: "approved" },
  rejected: { cls: "badge-danger", label: "rejected" },
};

export default function AdminApplications() {
  const [apps, setApps] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [creds, setCreds] = useState(null);   // shown once after approval
  const [rejecting, setRejecting] = useState(null);
  const [note, setNote] = useState("");

  const load = () => api.get("/admin/applications").then((r) => setApps(r.data));
  useEffect(() => { load(); }, []);

  const approve = async (a) => {
    if (!window.confirm(`Approve ${a.nursery_name}? This creates a live account.`)) return;
    try {
      const { data } = await api.put(`/admin/applications/${a.id}/approve`);
      setCreds({ ...data.credentials, nursery: a.nursery_name, owner: a.owner_name, phone: a.phone });
      load();
    } catch (e) {
      alert(e?.response?.data?.detail || "Approve failed");
    }
  };
  const submitReject = async () => {
    await api.put(`/admin/applications/${rejecting.id}/reject`, { note });
    setRejecting(null); setNote(""); load();
  };
  const del = async (a) => {
    if (!window.confirm("Delete this application permanently?")) return;
    await api.delete(`/admin/applications/${a.id}`);
    load();
  };

  const filtered = apps.filter((a) => filter === "all" || a.status === filter);
  const counts = {
    all: apps.length,
    pending: apps.filter((a) => a.status === "pending").length,
    approved: apps.filter((a) => a.status === "approved").length,
    rejected: apps.filter((a) => a.status === "rejected").length,
  };

  return (
    <div data-testid="admin-applications-page" className="fade-up">
      <BackToOverview />
      <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.16em", textTransform: "uppercase", color: "var(--secondary)" }}>The waiting list</div>
      <h1 className="page-title" style={{ marginTop: 8 }}>Founding Nursery Applications</h1>
      <p style={{ color: "var(--muted-fg)", marginTop: 6, fontSize: 14.5 }}>Pick your 5. The rest stay on the wait-list for the next round.</p>

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 22, marginBottom: 22 }}>
        {["pending", "approved", "rejected", "all"].map((k) => (
          <button key={k} onClick={() => setFilter(k)} className={`btn ${filter === k ? "btn-primary" : "btn-outline"}`} style={{ minHeight: 38, padding: "8px 16px", fontSize: 13 }} data-testid={`filter-${k}`}>
            {k} · {counts[k]}
          </button>
        ))}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(380px, 1fr))", gap: 16 }}>
        {filtered.map((a) => (
          <div key={a.id} className="card" style={{ padding: 22 }} data-testid={`app-card-${a.id}`}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
              <div style={{ flex: 1 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <h3 className="font-display" style={{ fontSize: 22 }}>{a.nursery_name}</h3>
                  <span className={`badge ${STATUS_BADGE[a.status]?.cls}`}>{STATUS_BADGE[a.status]?.label}</span>
                </div>
                <div style={{ fontSize: 13, color: "var(--muted-fg)", marginTop: 2 }}>{a.owner_name} · {a.crew_size} crew</div>
              </div>
              <div style={{ fontSize: 11, color: "var(--muted-fg)", textAlign: "right" }}>
                <Clock size={11} weight="bold" style={{ verticalAlign: -1 }} /> {new Date(a.created_at).toLocaleDateString()}
              </div>
            </div>

            <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 6, fontSize: 13.5 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}><EnvelopeSimple size={14} weight="fill" color="var(--primary)" /><a href={`mailto:${a.email}`} style={{ color: "var(--text)" }}>{a.email}</a></div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}><Phone size={14} weight="fill" color="var(--primary)" /><a href={`tel:${a.phone}`} style={{ color: "var(--text)" }}>{a.phone}</a></div>
              {a.address && <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}><MapPin size={14} weight="fill" color="var(--primary)" style={{ marginTop: 2 }} /><span style={{ color: "var(--text-soft)" }}>{a.address}</span></div>}
              {a.website && <div style={{ display: "flex", alignItems: "center", gap: 8 }}><Globe size={14} weight="fill" color="var(--primary)" /><a href={a.website} target="_blank" rel="noreferrer" style={{ color: "var(--secondary)", textDecoration: "underline" }}>{a.website.replace(/^https?:\/\//, "")}</a></div>}
            </div>

            <div style={{ marginTop: 14, padding: 14, background: "var(--bg-alt)", borderRadius: 12, fontSize: 13, lineHeight: 1.55 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "var(--secondary)", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 6 }}>Why they applied</div>
              <div style={{ color: "var(--text-soft)" }}>{a.why_now}</div>
              {a.top_plants && <div style={{ marginTop: 10, fontSize: 12 }}><b>Plants:</b> {a.top_plants}</div>}
              {a.current_method && <div style={{ marginTop: 6, fontSize: 12 }}><b>Today:</b> {a.current_method}</div>}
              {a.years_in_business && <div style={{ marginTop: 6, fontSize: 12 }}><b>Years:</b> {a.years_in_business}</div>}
              {a.referral && <div style={{ marginTop: 6, fontSize: 12 }}><b>Referral:</b> {a.referral}</div>}
            </div>

            {a.notes && (
              <div style={{ marginTop: 10, fontSize: 12, color: "var(--destructive)" }}><b>Reject note:</b> {a.notes}</div>
            )}

            <div style={{ marginTop: 14, display: "flex", gap: 8, flexWrap: "wrap" }}>
              {a.status === "pending" && (
                <>
                  <button className="btn btn-primary" onClick={() => approve(a)} style={{ minHeight: 38, padding: "8px 14px", fontSize: 13 }} data-testid={`approve-${a.id}`}>
                    <CheckCircle size={15} weight="fill" /> Approve & onboard
                  </button>
                  <button className="btn btn-outline" onClick={() => setRejecting(a)} style={{ minHeight: 38, padding: "8px 14px", fontSize: 13 }} data-testid={`reject-${a.id}`}>
                    <XCircle size={15} weight="bold" /> Reject
                  </button>
                </>
              )}
              {a.status === "approved" && (
                <span className="badge badge-success"><Star size={12} weight="fill" /> Account created — they're a Founding Nursery</span>
              )}
              {a.status === "rejected" && (
                <button className="btn btn-ghost" onClick={() => del(a)} style={{ minHeight: 38, padding: "8px 14px", fontSize: 13 }} data-testid={`del-${a.id}`}>
                  <Trash size={14} weight="bold" /> Remove
                </button>
              )}
            </div>
          </div>
        ))}
        {filtered.length === 0 && (
          <div className="card" style={{ padding: 36, textAlign: "center", color: "var(--muted-fg)" }}>
            No <b>{filter}</b> applications.
          </div>
        )}
      </div>

      {/* Credentials modal — shown once after approval */}
      {creds && (
        <Modal onClose={() => setCreds(null)} title="One-time credentials">
          <p style={{ fontSize: 14, color: "var(--text-soft)", lineHeight: 1.55 }}>
            Account created for <b>{creds.nursery}</b>. Share these credentials with {creds.owner} by phone or text — they will NOT be shown again.
          </p>
          <div style={{ marginTop: 16, padding: 18, background: "var(--bg-alt)", borderRadius: 14, fontFamily: "ui-monospace, monospace", fontSize: 15 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
              <span><b>Email:</b> {creds.email}</span>
              <button className="btn btn-ghost" style={{ minHeight: 30, padding: "4px 10px" }} onClick={() => navigator.clipboard?.writeText(creds.email)}><Copy size={14} weight="bold" /></button>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, marginTop: 10 }}>
              <span><b>Password:</b> {creds.password}</span>
              <button className="btn btn-ghost" style={{ minHeight: 30, padding: "4px 10px" }} onClick={() => navigator.clipboard?.writeText(creds.password)} data-testid="copy-pw-btn"><Copy size={14} weight="bold" /></button>
            </div>
          </div>
          {creds.phone && (
            <a href={`sms:${creds.phone}?body=${encodeURIComponent(`Welcome to NurserySense — you're in! Sign in at https://nurserysense.com/login with ${creds.email} / ${creds.password}. — NurserySense`)}`} className="btn btn-secondary" style={{ marginTop: 16, width: "100%", justifyContent: "center" }}>
              <Phone size={15} weight="bold" /> Text credentials to {creds.phone}
            </a>
          )}
          <button className="btn btn-primary" onClick={() => setCreds(null)} style={{ marginTop: 12, width: "100%", justifyContent: "center" }} data-testid="creds-close-btn">I've sent them — close</button>
        </Modal>
      )}

      {rejecting && (
        <Modal onClose={() => { setRejecting(null); setNote(""); }} title={`Reject ${rejecting.nursery_name}?`}>
          <p style={{ fontSize: 14, color: "var(--text-soft)" }}>Optional internal note about why (only you'll see this).</p>
          <textarea className="textarea" rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Not a good fit because…" style={{ marginTop: 10 }} data-testid="reject-note-input" />
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 14 }}>
            <button className="btn btn-outline" onClick={() => { setRejecting(null); setNote(""); }}>Cancel</button>
            <button className="btn btn-secondary" onClick={submitReject} data-testid="reject-confirm-btn"><XCircle size={15} weight="bold" /> Reject application</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

function Modal({ children, onClose, title }) {
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(42,51,42,0.45)", backdropFilter: "blur(4px)", display: "grid", placeItems: "center", zIndex: 60, padding: 16 }}>
      <div className="card-soft" style={{ background: "var(--surface)", padding: 24, width: "100%", maxWidth: 560, maxHeight: "90vh", overflow: "auto", borderRadius: 22 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <h3 className="font-display" style={{ fontSize: 24 }}>{title}</h3>
          <button className="btn btn-ghost" onClick={onClose}><X size={18} weight="bold" /></button>
        </div>
        {children}
      </div>
    </div>
  );
}
